import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Loader2, ArrowLeft, MessageCircle, Share2, Volume2, Pencil } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  fetchChantSync,
  fetchChantSlides,
  fetchChantStats,
  fetchTownhallInspiration,
  joinChanting,
  postChantProgress,
  fetchChantChat,
  sendChantChat,
  getChantSessionId,
  setChantSessionId,
  shareChantingNative,
  shareChantingWhatsApp,
  postChantPresence,
} from "@/lib/chanting/chanting-service";
import { resolveChantDeityImage } from "@/lib/chanting/deity-images";
import { fetchSlokas, fetchTownhallChant, resolveTownhallImageSrc } from "@/lib/townhall/townhall-service";
import { ScrollingSlokaPanel } from "@/components/townhall/ScrollingSlokaPanel";
import { VedaPathWatermark } from "@/components/brand/VedaPathWatermark";
import { useI18n } from "@/i18n/I18nContext";
import { useSacredAdmin } from "@/hooks/useSacredAdmin";

/** Phase GC — repetition presets (null = unlimited until user stops). */
const REPEAT_PRESETS = [3, 7, 10, 21, 108, null];

function pickSlide(slides, currentSeconds) {
  if (!slides?.length) return null;
  let active = slides[0];
  for (const s of slides) {
    if ((s.timestamp ?? 0) <= currentSeconds) active = s;
    else break;
  }
  return active;
}

function formatRepTarget(target) {
  if (target === null || target === undefined) return "∞";
  return String(target);
}

function DeityMurthy({ chantId, deity, gifRef, overrideSrc }) {
  const defaults = resolveChantDeityImage({ chantId, deity, gifRef });
  const candidates = [
    resolveTownhallImageSrc(overrideSrc),
    chantId ? resolveTownhallImageSrc(`/townhall/asset/${chantId}`) : null,
    resolveTownhallImageSrc(defaults),
  ].filter(Boolean);
  const [idx, setIdx] = useState(0);
  const src = candidates[Math.min(idx, candidates.length)] || null;
  const showImg = src && idx < candidates.length;

  return (
    <div className="relative inline-block mx-auto">
      <div
        className="w-32 h-32 rounded-full bg-gradient-to-br from-primary/20 via-amber-100/40 to-primary/10 flex items-center justify-center shadow-inner overflow-hidden"
        data-testid="deity-murthy"
        data-chant-id={chantId}
        data-deity={deity}
        aria-label={deity || chantId}
      >
        {showImg ? (
          <img
            key={src}
            src={src}
            alt={deity || chantId || ""}
            className="w-full h-full object-contain p-2"
            onError={() => setIdx((i) => i + 1)}
          />
        ) : (
          <span className="text-4xl font-sanskrit text-primary/80">ॐ</span>
        )}
      </div>
      <VedaPathWatermark className="absolute -bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap" />
    </div>
  );
}

/**
 * Phase GC — Global chanting room: synced Sanskrit audio + tri-language slides.
 */
export function GlobalChantingRoom({ token, mandirOverlay = false, chantId: chantIdProp, onCloseMandir }) {
  const { chantId: routeChantId } = useParams();
  const chantId = chantIdProp || routeChantId;
  const [searchParams] = useSearchParams();
  const { lang, t } = useI18n();
  const { isAdmin, canEditLibrary, isStaffRole } = useSacredAdmin();
  const canEditTownhall = isAdmin || canEditLibrary || isStaffRole;
  const privateMode =
    searchParams.get("private") === "1" || (mandirOverlay && searchParams.get("mode") !== "global");

  const [sync, setSync] = useState(null);
  const [slides, setSlides] = useState([]);
  const [deityOverride, setDeityOverride] = useState(null);
  const [session, setSession] = useState(null);
  const [joined, setJoined] = useState(false);
  const [textOnly, setTextOnly] = useState(false);
  const [targetRep, setTargetRep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [audioReady, setAudioReady] = useState(false);
  const [audioSeconds, setAudioSeconds] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(null);
  const [chatOpen, setChatOpen] = useState(!privateMode);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatRegion, setChatRegion] = useState("");
  const [completed, setCompleted] = useState(false);
  const [shareText, setShareText] = useState(null);
  const [timeSpent, setTimeSpent] = useState(0);
  const [inspiration, setInspiration] = useState(null);
  const [manualCount, setManualCount] = useState("");
  const [manualSaving, setManualSaving] = useState(false);
  const [countError, setCountError] = useState("");

  const audioRef = useRef(null);
  const tickRef = useRef(null);
  const lastCycleIdRef = useRef(null);
  const joinStartedRef = useRef(null);

  const deepLink = `${typeof window !== "undefined" ? window.location.origin : ""}/global-chanting/${chantId}`;

  const loadSync = useCallback(async () => {
    try {
      const data = await fetchChantSync(chantId);
      setSync(data);
      return data;
    } catch {
      setSync(null);
      return null;
    }
  }, [chantId]);

  const refreshStats = useCallback(async () => {
    if (!chantId || !session?.session_id) return;
    try {
      const stats = await fetchChantStats(chantId, session.session_id);
      if (stats.global_stats) {
        setSync((s) => (s ? { ...s, global_stats: stats.global_stats, personal: stats.personal } : s));
      }
      if (stats.personal) setSession(stats.personal);
      if (stats.inspiration) setInspiration(stats.inspiration);
    } catch {
      /* optional */
    }
  }, [chantId, session?.session_id]);

  useEffect(() => {
    if (!chantId) return;
    let cancelled = false;
    Promise.all([
      loadSync(),
      fetchSlokas(chantId, { lang }).catch(() => []),
      fetchTownhallChant(chantId, { lang }).catch(() => null),
      fetchChantSlides(chantId, lang).catch(() => ({ slides: [] })),
      fetchTownhallInspiration().catch(() => null),
    ])
      .then(([syncData, d1Slokas, townhall, slideData, inspirationData]) => {
        if (cancelled) return;
        const fromD1 = Array.isArray(d1Slokas) && d1Slokas.length ? d1Slokas : null;
        const fromTownhall = townhall?.slides?.length ? townhall.slides : null;
        const fromLegacy = slideData?.slides?.length ? slideData.slides : null;
        setSlides(fromD1 || fromTownhall || fromLegacy || []);
        if (townhall?.deity_image) setDeityOverride(townhall.deity_image);
        if (inspirationData) setInspiration(inspirationData);
        // No audio URL → text-only scroll mode is available once joined.
        const audioMissing = !syncData?.audio_file && !townhall?.audio_file;
        if (audioMissing) setTextOnly(true);
      })
      .catch(console.error)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [chantId, lang, loadSync]);

  const beginChanting = async () => {
    // Text-only when audio missing — still scroll transliteration on a timer.
    if (!sync?.audio_file) {
      setJoined(true);
      setTextOnly(true);
      setAudioSeconds(0);
      setAudioReady(false);
      return;
    }

    try {
      const sid = getChantSessionId();
      const join = await joinChanting({
        chantId,
        targetRepetitions: targetRep,
        privateMode,
        sessionId: sid,
        token,
      });
      setChantSessionId(join.session.session_id);
      setSession(join.session);
      setSync((s) => (s ? { ...s, global_stats: join.global_stats, cycle: join.cycle } : join));
      lastCycleIdRef.current = join.cycle.cycle_id;
      joinStartedRef.current = Date.now();
      setJoined(true);
      setTextOnly(false);
    } catch {
      setJoined(true);
      setTextOnly(true);
      setAudioSeconds(0);
    }
  };

  const startAudioAtOffset = useCallback(
    (offsetSeconds) => {
      const el = audioRef.current;
      if (!el || !sync?.audio_file || textOnly) return;
      el.currentTime = Math.min(offsetSeconds, (sync.cycle?.duration_seconds ?? 60) - 0.5);
      el.play().catch(() => {
        setTextOnly(true);
      });
      setAudioReady(true);
    },
    [sync, textOnly]
  );

  useEffect(() => {
    if (!sync?.cycle || loading || !joined || textOnly) return;
    if (sync.cycle.in_gap) return;
    startAudioAtOffset(sync.cycle.offset_seconds);
  }, [sync, loading, joined, textOnly, startAudioAtOffset]);

  // Text-only scroll: advance through sloka timestamps without audio.
  useEffect(() => {
    if (!joined || !textOnly || !slides?.length) return;
    const maxTs = Math.max(...slides.map((s) => Number(s.timestamp) || 0), 30);
    const started = Date.now();
    const id = setInterval(() => {
      const elapsed = ((Date.now() - started) / 1000) % (maxTs + 8);
      setAudioSeconds(elapsed);
      setCurrentSlide(pickSlide(slides, elapsed));
    }, 400);
    return () => clearInterval(id);
  }, [joined, textOnly, slides]);

  useEffect(() => {
    if (!joined || !session?.session_id) return;
    refreshStats();
    const id = setInterval(refreshStats, 30_000);
    return () => clearInterval(id);
  }, [joined, session?.session_id, refreshStats]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || textOnly) return;

    const onTime = () => {
      const tsec = el.currentTime;
      setAudioSeconds(tsec);
      setCurrentSlide(pickSlide(slides, tsec));
    };

    const onEnded = async () => {
      if (!session) return;
      try {
        const res = await postChantProgress({
          sessionId: session.session_id,
          chantId,
          cycleId: lastCycleIdRef.current,
          completedCycle: true,
          timeSpentSeconds: sync?.cycle?.duration_seconds,
          token,
        });
        setSession(res.session);
        if (res.global_stats && sync) {
          setSync((s) => ({ ...s, global_stats: res.global_stats }));
        }
        setTimeSpent(res.session?.total_time_spent_seconds ?? 0);

        if (res.completed) {
          setCompleted(true);
          setShareText(res.share_text);
          return;
        }

        const fresh = await loadSync();
        if (!fresh?.cycle) return;
        lastCycleIdRef.current = fresh.cycle.cycle_id;
        if (fresh.cycle.in_gap) {
          const wait = fresh.ms_until_next_cycle ?? 1000;
          setTimeout(async () => {
            const next = await loadSync();
            if (!next?.cycle) return;
            lastCycleIdRef.current = next.cycle.cycle_id;
            startAudioAtOffset(next.cycle.offset_seconds);
          }, wait);
        } else {
          startAudioAtOffset(fresh.cycle.offset_seconds);
        }
      } catch (e) {
        console.error(e);
      }
    };

    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnded);
    };
  }, [session, chantId, slides, sync, token, loadSync, startAudioAtOffset, textOnly]);

  useEffect(() => {
    if (!chatOpen || privateMode || !sync?.cycle) return;
    const poll = () => {
      fetchChantChat(chantId, sync.cycle.cycle_id)
        .then((r) => setChatMessages(r.messages || []))
        .catch(() => {});
    };
    poll();
    tickRef.current = setInterval(poll, 5000);
    return () => clearInterval(tickRef.current);
  }, [chatOpen, privateMode, chantId, sync?.cycle?.cycle_id]);

  useEffect(() => {
    if (!joined || !session?.session_id || privateMode) return;
    postChantPresence({ chantId, sessionId: session.session_id });
    const id = setInterval(() => {
      postChantPresence({ chantId, sessionId: session.session_id });
    }, 45_000);
    return () => clearInterval(id);
  }, [joined, session?.session_id, chantId, privateMode]);

  const globalCount = sync?.global_stats?.total_cycles_completed_today ?? 0;
  const globalParticipations = sync?.global_stats?.total_participations_today ?? 0;
  const activeNow = inspiration?.sadhakas_now ?? sync?.active_participants ?? 0;
  const totalChants = inspiration?.total_global_chants ?? globalCount;
  const personalDone = session?.completed_repetitions ?? 0;
  const personalTarget = session?.target_repetitions ?? targetRep;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Allow text-only Prayer Hall / Mandir when sync is unavailable but D1 slokas exist.
  if (!sync && !slides.length) {
    return (
      <div className="min-h-screen py-24 text-center">
        <p className="text-muted-foreground">Chant not found.</p>
        <Button asChild variant="link" className="mt-4">
          <Link to="/global-chanting">Back</Link>
        </Button>
      </div>
    );
  }

  const displayTitle = sync?.title || chantId?.replace(/_/g, " ") || "Chant";
  const sharePayload = {
    title: displayTitle,
    text: shareText || `Today I completed ${personalDone} cycles of ${displayTitle}. Join me in the next wave on VedaPath.`,
    url: deepLink,
  };

  return (
    <div
      className={`min-h-screen py-20 px-4 ${mandirOverlay ? "bg-transparent" : "noise bg-gradient-to-b from-background to-muted/40"}`}
      data-testid="global-chanting-room"
      data-chant-id={chantId}
      data-private={privateMode ? "1" : "0"}
    >
      <div className="max-w-2xl mx-auto">
        {mandirOverlay && onCloseMandir && (
          <Button variant="ghost" size="sm" className="mb-4" onClick={onCloseMandir}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Return to Dhyāna Mandir
          </Button>
        )}

        {!mandirOverlay && (
          <div className="flex items-center justify-between mb-4 gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/global-chanting">
                <ArrowLeft className="w-4 h-4 mr-1" /> All chants
              </Link>
            </Button>
            {canEditTownhall && (
              <Button size="sm" variant="outline" asChild data-testid="global-chant-edit-btn">
                <Link to={`/townhall/${chantId}/edit`}>
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Link>
              </Button>
            )}
          </div>
        )}

        <div className="text-center mb-6">
          <Badge variant="outline" className="mb-2">
            {privateMode ? "Private sādhana" : "Global LIVE"}
          </Badge>
          <h1 className="font-heading text-2xl">{displayTitle}</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Sanskrit original + transliteration in your language — same sound, scrolls with the chant
          </p>
        </div>

        <DeityMurthy
          chantId={chantId}
          deity={sync?.deity}
          gifRef={sync?.gif_ref}
          overrideSrc={deityOverride}
        />

        <div className="flex flex-wrap justify-center gap-4 my-6 text-sm">
          {!privateMode && (
            <Card className="bg-white/80 px-4 py-2">
              <p className="text-xs text-muted-foreground">Sādhakas now</p>
              <p className="font-medium">{activeNow}</p>
            </Card>
          )}
          {!privateMode && (
            <Card className="bg-white/80 px-4 py-2">
              <p className="text-xs text-muted-foreground">Global cycles today</p>
              <p className="font-medium">{globalCount.toLocaleString()}</p>
            </Card>
          )}
          {!privateMode && (
            <Card className="bg-white/80 px-4 py-2">
              <p className="text-xs text-muted-foreground">Participations today</p>
              <p className="font-medium">{globalParticipations.toLocaleString()}</p>
            </Card>
          )}
          <Card className="bg-white/80 px-4 py-2">
            <p className="text-xs text-muted-foreground">Your repetitions</p>
            <p className="font-medium">
              {personalDone} / {formatRepTarget(personalTarget)}
            </p>
          </Card>
          <Card className="bg-white/80 px-4 py-2">
            <p className="text-xs text-muted-foreground">Total chants</p>
            <p className="font-medium">{Number(totalChants || 0).toLocaleString()}</p>
          </Card>
          {timeSpent > 0 && (
            <Card className="bg-white/80 px-4 py-2">
              <p className="text-xs text-muted-foreground">Time chanting</p>
              <p className="font-medium">{Math.round(timeSpent / 60)} min</p>
            </Card>
          )}
        </div>

        {!joined && (
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            <span className="text-xs text-muted-foreground self-center w-full text-center">Repeat goal:</span>
            {REPEAT_PRESETS.map((n) => (
              <Button
                key={n === null ? "unlimited" : n}
                size="sm"
                variant={targetRep === n ? "default" : "outline"}
                onClick={() => setTargetRep(n)}
              >
                {n === null ? "∞" : `${n}×`}
              </Button>
            ))}
            <Button className="w-full mt-2" onClick={beginChanting}>
              Begin synced chanting
            </Button>
          </div>
        )}

        <Card className="bg-white/90 mb-4">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-2 justify-center">
              <div className="w-full sm:w-auto">
                <label className="text-xs text-muted-foreground">Enter my chant count</label>
                <input
                  value={manualCount}
                  onChange={(e) => setManualCount(e.target.value.replace(/[^\d]/g, ""))}
                  placeholder="108"
                  className="mt-1 h-9 w-full sm:w-40 rounded border border-input bg-background px-3 text-sm"
                />
              </div>
              <Button
                type="button"
                disabled={manualSaving || !manualCount || !session?.session_id}
                onClick={async () => {
                  setCountError("");
                  const count = Number(manualCount || 0);
                  if (!count || count < 1) {
                    setCountError("Enter a positive chant count.");
                    return;
                  }
                  try {
                    setManualSaving(true);
                    const res = await postChantProgress({
                      sessionId: session?.session_id,
                      chantId,
                      cycleId: lastCycleIdRef.current,
                      completedCycle: false,
                      token,
                      count,
                    });
                    setSession(res.session);
                    if (res.global_stats) {
                      setSync((s) => (s ? { ...s, global_stats: res.global_stats } : s));
                    }
                    const refreshed = await fetchTownhallInspiration().catch(() => null);
                    if (refreshed) setInspiration(refreshed);
                    setManualCount("");
                  } catch {
                    setCountError("Unable to save chant count right now.");
                  } finally {
                    setManualSaving(false);
                  }
                }}
              >
                {manualSaving ? "Saving..." : "Add count"}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={!session?.session_id}
                onClick={async () => {
                  try {
                    const res = await postChantProgress({
                      sessionId: session?.session_id,
                      chantId,
                      cycleId: lastCycleIdRef.current,
                      completedCycle: true,
                      token,
                      count: 1,
                    });
                    setSession(res.session);
                    if (res.global_stats) {
                      setSync((s) => (s ? { ...s, global_stats: res.global_stats } : s));
                    }
                    const refreshed = await fetchTownhallInspiration().catch(() => null);
                    if (refreshed) setInspiration(refreshed);
                  } catch {
                    setCountError("Unable to record repetition.");
                  }
                }}
              >
                Count 1 repetition
              </Button>
            </div>
            {countError ? <p className="mt-2 text-xs text-destructive text-center">{countError}</p> : null}
          </CardContent>
        </Card>

        <Card className="bg-white/90 mb-4">
          <CardContent className="p-6 space-y-4">
            <div className="flex justify-end gap-2">
              {joined && textOnly && (
                <Badge variant="secondary" data-testid="text-only-badge">
                  Text chanting
                </Badge>
              )}
              {!textOnly && sync?.audio_file && !audioReady && (
                <Button size="sm" onClick={() => startAudioAtOffset(sync.cycle?.offset_seconds ?? 0)}>
                  <Volume2 className="w-4 h-4 mr-1" /> {t("btn.listen") || "Enable Sanskrit audio"}
                </Button>
              )}
            </div>

            {sync?.cycle?.in_gap && !textOnly ? (
              <p className="text-center text-muted-foreground py-8">Next wave begins shortly…</p>
            ) : slides.length > 0 ? (
              <ScrollingSlokaPanel
                slides={slides}
                currentSeconds={
                  joined || audioReady || textOnly
                    ? audioSeconds
                    : sync?.cycle?.offset_seconds ?? 0
                }
                language={lang}
              />
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No sloka text yet — admins can add Sanskrit + transliteration via Edit.
              </p>
            )}
          </CardContent>
        </Card>

        {!textOnly && sync?.audio_file ? (
          <audio ref={audioRef} src={sync.audio_file} preload="auto" />
        ) : null}

        {completed && (
          <Card className="bg-primary/5 border-primary/20 mb-4" data-testid="chant-completion-card">
            <CardContent className="p-4 text-center space-y-3">
              <DeityMurthy
                chantId={chantId}
                deity={sync?.deity}
                gifRef={sync?.gif_ref}
                overrideSrc={deityOverride}
              />
              <p className="font-medium">
                Sādhana complete — {personalDone} cycles of {displayTitle} offered.
              </p>
              <p className="text-sm text-muted-foreground">{sharePayload.text}</p>
              <div className="flex flex-wrap justify-center gap-2">
                <Button size="sm" onClick={() => shareChantingNative(sharePayload)}>
                  <Share2 className="w-4 h-4 mr-1" /> Share
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => shareChantingWhatsApp(sharePayload)}
                  data-testid="whatsapp-share"
                >
                  WhatsApp
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <Link to="/prayer-hall">Return to Prayer Hall</Link>
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <Link to="/prayer-hall">Enter Dhyāna Mandir</Link>
                </Button>
                <Button size="sm" variant="ghost" onClick={() => window.location.reload()}>
                  Continue chanting
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {!privateMode && (
          <div className="mb-4">
            <Button variant="outline" size="sm" onClick={() => setChatOpen((v) => !v)}>
              <MessageCircle className="w-4 h-4 mr-1" />
              {chatOpen ? "Hide" : "Show"} anonymous chat
            </Button>
          </div>
        )}

        {chatOpen && !privateMode && (
          <Card className="bg-white/80" data-testid="chant-anonymous-chat">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-2">Anonymous · spiritual messages only</p>
              <div className="max-h-40 overflow-y-auto space-y-2 mb-3 text-sm">
                {chatMessages.map((m) => (
                  <p key={m.id} className="border-l-2 border-primary/20 pl-2">
                    {m.region && <span className="text-xs text-muted-foreground">{m.region} · </span>}
                    {m.text}
                  </p>
                ))}
              </div>
              <form
                className="flex flex-col gap-2"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!chatInput.trim()) return;
                  try {
                    await sendChantChat(chantId, {
                      text: chatInput,
                      region: chatRegion.trim() || undefined,
                      cycleId: sync.cycle.cycle_id,
                      privateMode,
                    });
                    setChatInput("");
                    const r = await fetchChantChat(chantId, sync.cycle.cycle_id);
                    setChatMessages(r.messages || []);
                  } catch {
                    /* moderated */
                  }
                }}
              >
                <div className="flex gap-2">
                  <input
                    className="w-24 text-sm border rounded px-2 py-1"
                    value={chatRegion}
                    onChange={(e) => setChatRegion(e.target.value)}
                    placeholder="Region"
                    maxLength={32}
                  />
                  <input
                    className="flex-1 text-sm border rounded px-2 py-1"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Offer a short prayer…"
                    maxLength={200}
                  />
                  <Button type="submit" size="sm">
                    Send
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

/** Dhyāna Mandir overlay — global or private chanting inside Prayer Hall context */
export function DhyanaMandirChantingOverlay({ token, chantId = "lalita_sahasranama", onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-background/95 backdrop-blur-sm"
      data-testid="dhyana-mandir-chant-overlay"
    >
      <GlobalChantingRoom token={token} mandirOverlay chantId={chantId} onCloseMandir={onClose} />
    </div>
  );
}
